/**
 * 送信ボタンは入力チェックの結果で押せなくせず、押したときに確かめる。
 *
 * ■ 何が起きていたか
 *
 * 送信ボタンを :disabled="ObserverProps.invalid" で押せなくしていた。
 * 入力チェックは入力欄から離れたとき（change）に走る設定なので、
 * **最後の欄に入力した直後は、まだ「未入力」のまま押せない。**
 * ボタンを押すと、そのとき初めて欄から離れて入力チェックが通り、
 * 押せるようになる——が、その1回目の押下はもう終わっている。
 * 利用者には「報告を押しても何も起きない」に見える
 * （予定超過残業の申請の作成で実際に踏んだ。2026-09-17）。
 *
 * ■ どうするか
 *
 * ボタンは押せるままにし、押したときに ValidationObserver の validate() を
 * 呼ぶ。通ればそのまま処理を続け、通らなければ欄の下に理由が出る。
 *
 * **通らなかったときは、最初に引っかかった欄まで画面を動かす。**
 * 長い画面では理由が押した場所から見えず、「押しても何も起きない」が
 * 残る（ユーザ更新で、画面の上の社員番号で止まっていた）。
 *
 * **押した瞬間から、同じフォームの次の押下を受け付けない。**
 * 入力チェックは待ちが入るので、その間にもう一度押すと2本目が走る。
 * 処理の中の submit_flg は処理に入ってからしか効かず、1本目の通信が
 * 終わって submit_flg が戻ったあとに2本目のチェックが終わると、2回送られる
 * （監査の指摘 K1、2026-09-17）。止めるのは処理が返した Promise が
 * 終わるまで。「保存」と「保存後、申請画面へ」のように同じフォームの
 * ボタンは、同じ枠の名前で1本にまとまる。
 *
 * **入力チェックの枠が見つからなければ、送らない。**
 * ref の書き間違いで、チェックなしに保存へ進むのを防ぐ（監査の指摘 K2）。
 *
 *   @click="whenValid('obs', apply)"
 *   @click="whenValid('obs', apply, false)"   // 引数を渡す
 */
export function firstInvalidElement (observer) {
  const providers = observer && observer.refs ? Object.values(observer.refs) : []
  const invalid = providers
    .filter(provider => provider && provider.errors && provider.errors.length && provider.$el)
    .map(provider => provider.$el)
  if (!invalid.length) return null
  // 画面の上から順に並べて、いちばん上を返す
  return invalid.sort((a, b) => a.getBoundingClientRect().top - b.getBoundingClientRect().top)[0]
}

/** 入力チェックに通らなかったとき、いちばん上で引っかかった欄まで画面を動かす。 */
export function scrollToFirstInvalid (vm, observer) {
  vm.$nextTick(() => {
    const element = firstInvalidElement(observer)
    if (element && typeof element.scrollIntoView === 'function') {
      element.scrollIntoView({ behavior: 'smooth', block: 'center' })
    }
  })
}

export default {
  methods: {
    whenValid (observerRef, handler, ...args) {
      const observer = this.$refs[observerRef]
      if (!observer || typeof observer.validate !== 'function') {
        console.error('[whenValid] 入力チェックの枠 "' + observerRef + '" が見つからないため、送信を止めました。')
        return Promise.resolve(undefined)
      }

      if (!this.$_whenValidBusy) this.$_whenValidBusy = {}
      const busy = this.$_whenValidBusy
      if (busy[observerRef]) return Promise.resolve(undefined)
      busy[observerRef] = true
      const release = () => { busy[observerRef] = false }

      return Promise.resolve()
        .then(() => observer.validate())
        .then(valid => {
          if (!valid) {
            scrollToFirstInvalid(this, observer)
            return undefined
          }
          return handler.apply(this, args)
        })
        .then(
          result => { release(); return result },
          error => { release(); throw error }
        )
    }
  }
}
